/**
 * „Klienci" (variant A): every client with a recording, newest recording first, under „Wszyscy
 * klienci". A row narrows the wall; its arrow, on hover or focus, opens the client's card on Forma.
 */

import { Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import type { SkeletonGate } from '~/shared/lib/useSkeletonGate';
import { AvatarChip } from '~/shared/ui/AvatarChip';
import skel from '~/shared/ui/skeleton.module.css';
import hidden from '~/shared/ui/visuallyHidden.module.css';

import type { RecordingsView } from '../model/recordings';

import { OutGlyph, PeopleGlyph } from './RecordingsIcons';
import styles from './RecordingsScreen.module.css';

const SKELETON_ROWS = 6;

interface RecordingsClientListProps {
  readonly view: RecordingsView;
  readonly gate: SkeletonGate;
  readonly failed: boolean;
  readonly customerId: number | null;
  readonly onPick: (customerId: number | null) => void;
}

export function RecordingsClientList({ view, gate, failed, customerId, onPick }: RecordingsClientListProps) {
  const { t } = useTranslation();

  return (
    <nav aria-busy={gate.waiting} aria-label={t('web:recordings.clientsLabel')} className={styles.clients}>
      <div className={styles.clientsHead}>
        <h2 className={styles.clientsTitle}>{t('web:recordings.clientsTitle')}</h2>
        <span className={styles.clientsOrder}>{t('web:recordings.clientsOrder')}</span>
      </div>

      {gate.waiting ? (
        <div className={styles.clientRows}>
          <span className={hidden.srOnly}>{t('web:recordings.loading')}</span>
          {gate.skeleton
            ? Array.from({ length: SKELETON_ROWS }, (_, index) => (
                <span className={styles.skelRow} key={index}>
                  <i className={`${String(skel.bar)} ${String(skel.disc)} ${String(styles.skelAvatar)}`} />
                  <span className={styles.skelLines}>
                    <i className={`${String(skel.bar)} ${String(styles.skelName)}`} />
                    <i className={`${String(skel.bar)} ${String(styles.skelMeta)}`} />
                  </span>
                </span>
              ))
            : null}
        </div>
      ) : failed ? (
        <p className={`${styles.clientsNotice} ${gate.reveal}`}>{t('web:recordings.failed')}</p>
      ) : (
        <ul className={`${styles.clientRows} ${gate.reveal}`}>
          <li className={styles.allRow}>
            <button
              aria-current={customerId === null ? 'true' : undefined}
              className={styles.row}
              onClick={() => {
                onPick(null);
              }}
              type="button"
            >
              <span className={styles.allMark}>
                <PeopleGlyph className={styles.allGlyph} />
              </span>
              <span className={styles.rowName}>{t('web:recordings.allClients')}</span>
              <span className={styles.rowMeta}>{view.people}</span>
              <span className={styles.rowCount}>{view.total}</span>
            </button>
          </li>
          {view.clients.map((client) => (
            <li className={styles.clientRow} key={client.customerId}>
              <button
                aria-current={customerId === client.customerId ? 'true' : undefined}
                className={styles.row}
                onClick={() => {
                  onPick(client.customerId);
                }}
                type="button"
              >
                <AvatarChip
                  avatarUrl={client.person.avatarUrl}
                  className={styles.rowAvatar}
                  initials={client.person.initials}
                  loading="lazy"
                  variant="clip"
                />
                <span className={styles.rowName}>
                  <span className={styles.rowNameText}>{client.person.fullName}</span>
                  {client.unread ? (
                    <span className={styles.unreadDot}>
                      <span className={hidden.srOnly}>{t('web:recordings.unread')}</span>
                    </span>
                  ) : null}
                </span>
                <span className={styles.rowMeta}>{client.meta}</span>
                <span className={styles.rowCount}>{client.count}</span>
              </button>
              <Link
                aria-label={t('web:recordings.openCardOf', { name: client.person.fullName })}
                className={styles.rowGo}
                params={{ clientId: String(client.customerId) }}
                search={{ tab: 'forma' }}
                title={t('web:recordings.openCard')}
                to="/klienci/$clientId"
              >
                <OutGlyph className={styles.goGlyph} />
              </Link>
            </li>
          ))}
        </ul>
      )}
    </nav>
  );
}
